/**
 * The id a connection-dialog probe keys EVERYTHING on — the pool, the SSH
 * tunnel, the Oracle pool gate — so the live connection is never reused,
 * joined or disconnected by a probe. Shared between the two processes: the
 * renderer mints it for "Test connection" and "Load databases"
 * (`NewConnectionPopup.tsx`), the main process recognises it (the stepwise
 * runner, the managers' residue eviction, the Oracle gate) and derives from it
 * the ONE thing a probe borrows from a saved connection — whose STORED SSH
 * secrets it may read (`sshTunnelManager`): `parseProbeConnectionId(id)?.liveId`.
 *
 * Shape: `probe:<uuid>:<liveId>`. The live id comes LAST: the managers scan
 * their per-database maps with `key.startsWith(`${id}:`)`, and an id that
 * began with the live id (`<liveId>::probe::…`) matched the live connection's
 * scans. A saved connection id is a UUID and never starts with `probe:`.
 *
 * A brand-new connection has nothing stored, so its probe embeds a fresh uuid
 * that owns nothing. A value that starts with `probe:` but does not parse —
 * no uuid nonce, an empty live id, a live id that contains ':' or is itself a
 * probe id (a nested or crafted one) — is not a probe id: it is treated as a
 * live id everywhere and resolves its own — nonexistent — secrets. The parse
 * is total over any input (an IPC argument is whatever the renderer sent).
 */
import { v4 as uuidv4 } from 'uuid';

const PROBE_ID_PREFIX = 'probe:';
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function buildProbeConnectionId(liveId: string): string {
  return `${PROBE_ID_PREFIX}${uuidv4()}:${liveId}`;
}

/**
 * The live id a probe id embeds, or `undefined` for anything that is not a
 * well-formed probe id. Typed for the ids callers hold (a string, or none);
 * the runtime `typeof` guard stays because an IPC argument is whatever the
 * renderer sent.
 */
export function parseProbeConnectionId(connectionId: string | null | undefined): { liveId: string } | undefined {
  if (typeof connectionId !== 'string' || !connectionId.startsWith(PROBE_ID_PREFIX)) return undefined;
  const rest = connectionId.slice(PROBE_ID_PREFIX.length);
  const separator = rest.indexOf(':');
  if (separator < 0) return undefined;
  const nonce = rest.slice(0, separator);
  const liveId = rest.slice(separator + 1);
  if (!UUID_RE.test(nonce) || liveId.length === 0) return undefined;
  // A live id never contains ':' (a saved id is a uuid); one that does — a
  // probe id nested in another, or a crafted one — parses as nothing.
  if (liveId.includes(':')) return undefined;
  return { liveId };
}

export function isProbeConnectionId(connectionId: string | null | undefined): boolean {
  return parseProbeConnectionId(connectionId) !== undefined;
}
